import { useMemo, useRef, useEffect, useLayoutEffect, useCallback } from 'react'
import {
  buildBracketPartsFromHtml,
  mergeBracketPartsToSigningHtml,
} from '../lib/agreementBracketFields'

const BF_INPUT_SELECTOR = 'input.agreement-bracket-input[data-bf-slot]'

/**
 * Recipient signing: one sanitized HTML shell with real inline inputs where [fields] were,
 * so inputs stay inside headings/sentences (splitting HTML + React siblings broke layout).
 *
 * @param {string} sanitizedHtml ,  DOMPurify-sanitized agreement HTML
 * @param {Record<number, string>} values
 * @param {(next: Record<number, string> | ((prev: Record<number, string>) => Record<number, string>)) => void} onValuesChange
 * @param {number[]} invalidFieldIds ,  slot indices to show error styling (empty required fields)
 */
export function RecipientBracketAgreement({ sanitizedHtml, values, onValuesChange, invalidFieldIds = [] }) {
  const parts = useMemo(() => buildBracketPartsFromHtml(sanitizedHtml, true), [sanitizedHtml])
  const signingHtml = useMemo(() => mergeBracketPartsToSigningHtml(parts), [parts])
  const shellRef = useRef(null)

  useLayoutEffect(() => {
    const el = shellRef.current
    if (!el) return
    el.innerHTML = signingHtml
  }, [signingHtml])

  useEffect(() => {
    const root = shellRef.current
    if (!root) return
    root.querySelectorAll(BF_INPUT_SELECTOR).forEach((input) => {
      const i = Number(input.getAttribute('data-bf-slot'))
      if (Number.isNaN(i)) return
      const v = values[i] ?? ''
      if (input.value !== v) input.value = v
    })
  }, [values, signingHtml])

  useEffect(() => {
    const root = shellRef.current
    if (!root) return
    const invalidSet = new Set(invalidFieldIds)
    root.querySelectorAll(BF_INPUT_SELECTOR).forEach((input) => {
      const i = Number(input.getAttribute('data-bf-slot'))
      input.classList.toggle('agreement-input-error', invalidSet.has(i))
      input.setAttribute('aria-invalid', invalidSet.has(i) ? 'true' : 'false')
    })
  }, [invalidFieldIds, signingHtml])

  const onInput = useCallback(
    (e) => {
      const t = e.target
      if (!t || t.tagName !== 'INPUT' || !t.matches(BF_INPUT_SELECTOR)) return
      const i = Number(t.getAttribute('data-bf-slot'))
      if (Number.isNaN(i)) return
      onValuesChange((prev) => ({ ...prev, [i]: t.value }))
    },
    [onValuesChange],
  )

  return (
    <div
      ref={shellRef}
      className="agreement-text agreement-text--recipient-fields"
      role="article"
      onInput={onInput}
    />
  )
}
